// src/Messanger/AssignTaskModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { api } from "./api";
import "./AssignTaskModal.css";

const norm = (s) =>
  String(s || "")
    .trim()
    .toLowerCase();

const isClientLike = (role) => {
  const r = norm(role);
  return r === "client" || r === "customer";
};

const catAlias = (s) => {
  const n = norm(s);
  if (n === "cad") return "canada";
  if (n === "archind" || n === "arch.ind" || n === "arch.ind.")
    return "arch.ind";
  return n;
};

const getProjName = (p) =>
  (p?.projectName || p?.name || "Untitled Project").trim();

const getId = (u) => (typeof u === "string" ? u : u?._id || u?.id || "");

/* ---------------- Compact MultiSelect ---------------- */
function MultiSelectDropdown({
  options = [],
  value = [],
  onChange,
  disabled = false,
  placeholder = "Select...",
  maxPreview = 2,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const allSelected = value.length === options.length && options.length > 0;

  const toggle = (opt) => {
    const exists = value.includes(opt);
    const next = exists ? value.filter((v) => v !== opt) : [...value, opt];
    onChange?.(next);
  };
  const selectAll = () => onChange?.(options);
  const clearAll = () => onChange?.([]);

  const preview =
    value.length === 0
      ? placeholder
      : value.length <= maxPreview
      ? value.join(", ")
      : `${value.slice(0, maxPreview).join(", ")} +${
          value.length - maxPreview
        }`;

  return (
    <div ref={wrapRef} className="atm-ms">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`atm-ms-trigger${
          value.length ? "" : " atm-ms-trigger--empty"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {preview}
      </button>

      {open && !disabled && (
        <div role="listbox" className="atm-ms-panel">
          <div className="atm-ms-bar">
            <button
              type="button"
              onClick={selectAll}
              disabled={allSelected || options.length === 0}
              className="atm-ms-chip-btn"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={value.length === 0}
              className="atm-ms-chip-btn"
            >
              Clear
            </button>
            <div className="atm-ms-count">{value.length} selected</div>
          </div>

          {options.length === 0 ? (
            <div className="atm-ms-empty">No options.</div>
          ) : (
            options.map((opt) => (
              <label key={opt} className="atm-ms-option">
                <input
                  type="checkbox"
                  checked={value.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span>{opt}</span>
              </label>
            ))
          )}

          <div className="atm-ms-footer">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="atm-ms-done"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Main modal ------------------------------ */
export default function AssignTaskModal({
  project,
  onClose,
  onAssigned,
  users = [],
  drawingMaster = [],
  includeClients = false,
  fetchUsersIfMissing = true,
}) {
  const [assigneeId, setAssigneeId] = useState("");
  const [typeofProject, setTypeofProject] = useState("");
  const [workType, setWorkType] = useState("");
  const [docs, setDocs] = useState([]);
  const [remark, setRemark] = useState("");
  const [dueDate, setDueDate] = useState("");

  const [localUsers, setLocalUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [localDM, setLocalDM] = useState([]);
  const [loadingDM, setLoadingDM] = useState(false);

  // Reset on project change
  useEffect(() => {
    setAssigneeId("");
    setTypeofProject("");
    setWorkType("");
    setDocs([]);
    setRemark("");
    setDueDate("");
  }, [project?._id]);

  // ------- Users (fallback fetch) -------
  const effectiveUsers = users?.length ? users : localUsers;

  useEffect(() => {
    if (!fetchUsersIfMissing) return;
    if (users?.length) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingUsers(true);
        const res = await api.get("/users", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!mounted) return;
        const arr = Array.isArray(res.data) ? res.data : res.data?.users || [];
        setLocalUsers(Array.isArray(arr) ? arr : []);
      } catch {
        if (mounted) setLocalUsers([]);
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [users, fetchUsersIfMissing]);

  // ------- Drawing Master source -------
  const dmSource = drawingMaster?.length ? drawingMaster : localDM;
  useEffect(() => {
    if (drawingMaster?.length) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingDM(true);
        const res = await api.get("/drawing-master", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!mounted) return;
        setLocalDM(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setLocalDM([]);
      } finally {
        if (mounted) setLoadingDM(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawingMaster]);

  // ------- Assignee lists -------
  const projectMemberIds = useMemo(() => {
    const arr = Array.isArray(project?.assignedUsers)
      ? project.assignedUsers
      : [];
    return arr.map((u) => getId(u)).filter(Boolean);
  }, [project]);

  const byId = useMemo(() => {
    return new Map((effectiveUsers || []).map((u) => [getId(u), u]));
  }, [effectiveUsers]);

  const assignedUserObjects = useMemo(() => {
    const raw = Array.isArray(project?.assignedUsers)
      ? project.assignedUsers
      : [];
    return raw
      .map((u) => {
        const id = getId(u);
        const full = byId.get(id);
        return full || (typeof u === "object" ? u : { _id: id });
      })
      .filter(Boolean);
  }, [project, byId]);

  const members = useMemo(() => {
    const list = assignedUserObjects
      .filter((u) => (includeClients ? true : !isClientLike(u.role)))
      .map((u) => ({
        ...u,
        _id: getId(u),
        label: u.name || u.email || getId(u),
      }));
    list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return list;
  }, [assignedUserObjects, includeClients]);

  const others = useMemo(() => {
    const setMembers = new Set(projectMemberIds);
    const nonClient = (effectiveUsers || []).filter((u) =>
      includeClients ? true : !isClientLike(u.role)
    );
    const list = nonClient
      .filter((u) => !setMembers.has(getId(u)))
      .map((u) => ({
        ...u,
        _id: getId(u),
        label: `${u.name || u.email || getId(u)} (not in project)`,
      }));
    list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return list;
  }, [effectiveUsers, projectMemberIds, includeClients]);

  const noUsers = members.length === 0 && others.length === 0;

  // ------- Drawing Master per category -------
  const projCat = catAlias(project?.category);
  const baseDM = Array.isArray(dmSource) ? dmSource : [];

  const dmForProject = useMemo(() => {
    if (!projCat || projCat === "all") return baseDM;
    const byCat = baseDM.filter((d) => catAlias(d.category) === projCat);
    return byCat.length ? byCat : baseDM;
  }, [baseDM, projCat]);

  const typeOptions = useMemo(
    () => [
      ...new Set(dmForProject.map((d) => d.typeofProject).filter(Boolean)),
    ],
    [dmForProject]
  );
  const workTypeOptions = useMemo(
    () => [...new Set(dmForProject.map((d) => d.workType).filter(Boolean))],
    [dmForProject]
  );
  const docOptions = useMemo(
    () => [
      ...new Set(dmForProject.map((d) => d.drawingDocument).filter(Boolean)),
    ],
    [dmForProject]
  );

  const estimated = useMemo(() => {
    const map = new Map();
    dmForProject.forEach((d) =>
      map.set(d.drawingDocument, parseFloat(d.estimatedHours) || 0)
    );
    const sum = (docs || []).reduce((s, name) => s + (map.get(name) || 0), 0);
    return Number(sum.toFixed(2));
  }, [dmForProject, docs]);

  const handleAssign = async () => {
    if (!assigneeId) return alert("Please select a user to assign.");
    if (!typeofProject) return alert("Please select Type of Project.");
    const selectedDocs = (docs || []).filter((d) => docOptions.includes(d));
    if (!selectedDocs.length)
      return alert("Please select at least one Drawing & Document.");

    const payload = {
      projectId: project?._id,
      assignedTo: assigneeId,
      typeofProject,
      ...(workType ? { workType } : {}),
      drawingDocuments: selectedDocs,
      estimatedHours: estimated,
      remark,
      dueDate: dueDate ? dayjs(dueDate, "YYYY-MM-DD").toDate() : undefined,
    };

    // Find assignee name for display
    const allUserChoices = [...members, ...others];
    const assigneeObj =
      allUserChoices.find((u) => getId(u) === assigneeId) || null;
    const assignedToName =
      assigneeObj?.name || assigneeObj?.label || assigneeId;

    try {
      // 1) Create the task
      await api.post("/tasks", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      // 2) Post a chat message of type 'task'
      const msgPayload = {
        type: "task",
        content: `📝 Task assigned to ${assignedToName}`,
        task: {
          assignedToId: assigneeId,
          assignedToName,
          typeofProject,
          workType: workType || undefined,
          drawingDocuments: selectedDocs,
          estimatedHours: estimated,
          dueDate: payload.dueDate || undefined,
          remark: remark || undefined,
        },
      };

      await api.post(`/project-messages/${project?._id}/messages`, msgPayload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      // 3) Fire a local event so chat updates instantly (before socket echo)
      window.dispatchEvent(
        new CustomEvent("project:message:local", {
          detail: {
            projectId: project?._id,
            message: {
              ...msgPayload,
              createdAt: new Date().toISOString(),
            },
          },
        })
      );

      alert("✅ Task assigned");
      window.dispatchEvent(
        new CustomEvent("tasks:changed", {
          detail: { projectId: project?._id },
        })
      );
      onAssigned?.();
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const url = err?.config?.url;
      const code = err?.code;
      const body =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message ||
            JSON.stringify(err?.response?.data || {});
      alert(
        `❌ Failed to assign task.\n` +
          [
            status && `HTTP ${status}`,
            url && `POST ${url}`,
            code && `code=${code}`,
            body && `server says: ${body}`,
          ]
            .filter(Boolean)
            .join(" | ")
      );
    }
  };

  const dmBlocking = loadingDM && !baseDM.length;
  const assignDisabled =
    dmBlocking || noUsers || (loadingUsers && !effectiveUsers.length);

  return (
    <div className="atm-overlay" onClick={onClose}>
      <div className="atm-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="atm-header">
          <div>
            <div className="atm-eyebrow">Assign Task</div>
            <h3 className="atm-title">
              {getProjName(project)}
              {project?.category && (
                <span className="atm-title-cat"> ({project.category})</span>
              )}
            </h3>
          </div>
          <button
            type="button"
            className="atm-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="atm-body">
          <div className="atm-field">
            <label className="atm-label">Assign to</label>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="atm-input"
              disabled={loadingUsers && !effectiveUsers.length}
            >
              <option value="">
                {loadingUsers && !effectiveUsers.length
                  ? "Loading users..."
                  : noUsers
                  ? "No users available"
                  : "Select..."}
              </option>

              {members.length > 0 && (
                <optgroup label="Project members">
                  {members.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.label}
                    </option>
                  ))}
                </optgroup>
              )}

              {others.length > 0 && (
                <optgroup
                  label={members.length > 0 ? "Other users" : "All users"}
                >
                  {others.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {noUsers && !(loadingUsers && !effectiveUsers.length) && (
              <div className="atm-hint">
                Tip: Pass the <code>users</code> prop or keep{" "}
                <code>fetchUsersIfMissing</code> enabled.
                {includeClients
                  ? ""
                  : " (Clients are hidden. Set includeClients={true} to show them.)"}
              </div>
            )}
          </div>

          <div className="atm-row">
            <div className="atm-field">
              <label className="atm-label">Type of Project</label>
              <select
                value={typeofProject}
                onChange={(e) => setTypeofProject(e.target.value)}
                disabled={dmBlocking}
                className="atm-input"
              >
                <option value="">
                  {dmBlocking ? "Loading..." : "Select..."}
                </option>
                {typeOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            <div className="atm-field">
              <label className="atm-label">Work Type (optional)</label>
              <select
                value={workType}
                onChange={(e) => setWorkType(e.target.value)}
                disabled={dmBlocking}
                className="atm-input"
              >
                <option value="">
                  {dmBlocking ? "Loading..." : "Select..."}
                </option>
                {workTypeOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="atm-field">
            <label className="atm-label">Drawing &amp; Document</label>
            <MultiSelectDropdown
              options={docOptions}
              value={docs}
              onChange={setDocs}
              disabled={dmBlocking}
              placeholder={dmBlocking ? "Loading..." : "Select..."}
            />
          </div>

          <div className="atm-row">
            <div className="atm-field">
              <label className="atm-label">Estimated Hours (auto-sum)</label>
              <input
                value={estimated}
                readOnly
                className="atm-input atm-input--readonly"
              />
            </div>

            <div className="atm-field">
              <label className="atm-label">Due Date (optional)</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="atm-input"
              />
            </div>
          </div>

          <div className="atm-field">
            <label className="atm-label">Remark</label>
            <input
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Optional note..."
              className="atm-input"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="atm-footer">
          <button
            type="button"
            onClick={onClose}
            className="atm-btn atm-btn--ghost"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={assignDisabled}
            className="atm-btn atm-btn--primary"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}